import React, { useState, useMemo, useEffect } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { Avatar } from "../shared/Avatar";
import {
  apiGenerateReceipt,
  apiGenerateReceiptBatch,
  apiReceiptEligibleMembers,
  apiListGiving,
} from "./api";
import { generateReceiptPdf } from "./TaxReceiptPdf";

// ============================================================
// ReceiptGenerateModal — single-member receipt generation.
// ============================================================
export function ReceiptGenerateModal({ open, members, currencySymbol, appSettings, onClose, onGenerated }) {
  const today = new Date();
  const [memberId, setMemberId] = useState("");
  const [search, setSearch]     = useState("");
  const [dateFrom, setDateFrom] = useState(`${today.getFullYear()}-01-01`);
  const [dateTo,   setDateTo]   = useState(`${today.getFullYear()}-12-31`);
  const [format,   setFormat]   = useState(appSettings?.receiptSettings?.receiptFormat || "itemized");
  const [preview,  setPreview]  = useState({ count: 0, totalCents: 0, loading: false });
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState("");
  const [done, setDone]         = useState(null);

  const activeMembers = useMemo(() => {
    return (members || [])
      .filter(m => m.status !== "inactive" && !m.archived)
      .sort((a, b) => {
        const la = (a.lastName || "").toLowerCase();
        const lb = (b.lastName || "").toLowerCase();
        if (la !== lb) return la < lb ? -1 : 1;
        return (a.firstName || "").localeCompare(b.firstName || "");
      });
  }, [members]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeMembers.slice(0, 40);
    return activeMembers.filter(m => `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase().includes(q)).slice(0, 40);
  }, [activeMembers, search]);

  // Refresh preview when member / date range changes.
  useEffect(() => {
    if (!open || !memberId) { setPreview({ count: 0, totalCents: 0, loading: false }); return; }
    let cancel = false;
    setPreview(p => ({ ...p, loading: true }));
    apiListGiving({ isVoided: "false", memberId, dateFrom, dateTo })
      .then(rows => {
        if (cancel) return;
        const eligible = (rows || []).filter(r => !r.isAnonymous && !r.voidedAt);
        setPreview({
          count: eligible.length,
          totalCents: eligible.reduce((s, r) => s + (Number(r.amountCents) || 0), 0),
          loading: false,
        });
      })
      .catch(() => { if (!cancel) setPreview({ count: 0, totalCents: 0, loading: false }); });
    return () => { cancel = true; };
  }, [open, memberId, dateFrom, dateTo]);

  if (!open) return null;

  const selectedMember = activeMembers.find(m => m.id === memberId);

  const generate = async () => {
    setBusy(true); setError("");
    try {
      const rec = await apiGenerateReceipt({ memberId, dateFrom, dateTo, format });
      // Immediately produce the PDF for download.
      await generateReceiptPdf({ receipt: rec, appSettings, currencySymbol });
      setDone(rec);
      onGenerated && onGenerated();
    } catch (e) {
      setError(e?.message || "Could not generate receipt");
    } finally { setBusy(false); }
  };

  const closeAndReset = () => {
    setMemberId(""); setSearch("");
    setDateFrom(`${today.getFullYear()}-01-01`); setDateTo(`${today.getFullYear()}-12-31`);
    setDone(null); setError("");
    onClose && onClose();
  };

  const money = (c) => `${currencySymbol}${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && closeAndReset()}
      data-testid="finance-receipt-single-modal"
      style={{ alignItems: "flex-start", paddingTop: 12, paddingBottom: 84 }}
    >
      <div className="modal" style={{ width: "min(96vw, 520px)", maxHeight: "calc(100vh - 96px)", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ position: "sticky", top: 0, background: "inherit", zIndex: 2 }}>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <CustomIcon name="document" size={18} /> Generate Tax Receipt
          </h3>
          <button className="modal-close" onClick={closeAndReset} data-testid="finance-receipt-single-close">×</button>
        </div>

        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          {!done ? (
            <>
              {/* Member selector */}
              <div className="form-group mb-14">
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.75 }}>Member</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search by name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="finance-receipt-single-search"
                />
                <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 6, border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", borderRadius: 8 }}>
                  {filteredMembers.length === 0 ? (
                    <div style={{ padding: 12, fontSize: 12, opacity: 0.6, textAlign: "center" }}>No members match.</div>
                  ) : filteredMembers.map(m => {
                    const isSel = m.id === memberId;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setMemberId(m.id)}
                        data-testid={`finance-receipt-single-mem-${m.id}`}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          width: "100%", padding: "8px 10px", background: isSel ? "color-mix(in srgb, var(--kc-accent) 18%, transparent)" : "transparent",
                          border: "none", borderBottom: "1px solid rgba(255,255,255,0.05)",
                          color: isSel ? "var(--kc-accent)" : "#FFFFFF", cursor: "pointer", textAlign: "left",
                        }}
                      >
                        <Avatar name={`${m.firstName} ${m.lastName}`} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{ width: 26, height: 26, fontSize: 12 }} />
                        <span style={{ fontSize: 13, fontWeight: isSel ? 700 : 500 }}>{m.lastName}, {m.firstName}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date range */}
              <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.75 }}>Date From</label>
                  <input type="date" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="finance-receipt-single-from" />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.75 }}>Date To</label>
                  <input type="date" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="finance-receipt-single-to" />
                </div>
              </div>

              {/* Format toggle */}
              <div className="form-group mb-14">
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.75 }}>Format</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                  {["itemized", "total_only"].map(opt => {
                    const active = format === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setFormat(opt)}
                        data-testid={`finance-receipt-single-format-${opt}`}
                        style={{
                          flex: "1 1 120px", padding: "8px 12px",
                          borderRadius: 8, border: `1px solid ${active ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 30%, transparent)"}`,
                          background: active ? "color-mix(in srgb, var(--kc-accent) 20%, transparent)" : "transparent",
                          color: active ? "var(--kc-accent)" : "var(--kc-sub)",
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                        }}
                      >
                        {opt === "itemized" ? "Itemized" : "Total Only"}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Preview */}
              {memberId && (
                <div
                  data-testid="finance-receipt-single-preview"
                  style={{
                    padding: 12, borderRadius: 10, background: "color-mix(in srgb, var(--kc-accent) 12%, transparent)",
                    border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", marginBottom: 14,
                  }}
                >
                  <div style={{ fontSize: 10, opacity: 0.65, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Preview</div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    <b>{selectedMember?.firstName} {selectedMember?.lastName}</b> — <b data-testid="finance-receipt-single-preview-count">{preview.count}</b> transactions
                  </div>
                  <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-accent)", marginTop: 4 }} data-testid="finance-receipt-single-preview-total">
                    {money(preview.totalCents)}
                  </div>
                  {preview.count === 0 && !preview.loading && (
                    <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 4 }}>
                      No eligible transactions in this range. Adjust the dates or pick another member.
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div data-testid="finance-receipt-single-error" style={{ background: "rgba(248,113,113,0.15)", color: "#f87171", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 10 }}>
                  {error}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "18px 8px" }} data-testid="finance-receipt-single-done">
              <div style={{ fontSize: 46, color: "#4ade80" }}>✓</div>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 20, fontWeight: 700, marginTop: 6 }}>Receipt Generated</div>
              <div style={{ fontSize: 13, marginTop: 8 }}>
                <div><b>Receipt No:</b> <span data-testid="finance-receipt-single-done-number">{done.receiptNumber}</span></div>
                <div style={{ marginTop: 4 }}><b>Total:</b> <span style={{ color: "var(--kc-accent)", fontFamily: "'Cinzel',serif", fontSize: 16 }}>{money(done.totalCents)}</span></div>
                <div style={{ fontSize: 11, opacity: 0.65, marginTop: 6 }}>PDF downloaded. Member has been notified in-app.</div>
              </div>
            </div>
          )}
        </div>

        <div style={{
          position: "sticky", bottom: 0, background: "var(--kc-surface)",
          borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 22%, transparent)", padding: "12px 16px",
          display: "flex", gap: 10, alignItems: "center",
        }}>
          {!done ? (
            <>
              <button className="btn btn-outline btn-sm" onClick={closeAndReset} data-testid="finance-receipt-single-cancel">Cancel</button>
              <div style={{ flex: 1 }} />
              <button
                className="btn"
                data-testid="finance-receipt-single-generate"
                onClick={generate}
                disabled={busy || !memberId || preview.count === 0}
                style={{
                  background: (!busy && memberId && preview.count > 0) ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 35%, transparent)",
                  color: "#111", fontWeight: 800, minHeight: 44, padding: "0 22px", borderRadius: 10,
                  cursor: (busy || !memberId || preview.count === 0) ? "not-allowed" : "pointer",
                }}
              >
                {busy ? "Generating…" : "Generate Receipt"}
              </button>
            </>
          ) : (
            <button className="btn btn-primary" style={{ marginLeft: "auto" }} onClick={closeAndReset} data-testid="finance-receipt-single-close-done">Done</button>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// ReceiptBatchModal — year-end batch generation.
// ============================================================
export function ReceiptBatchModal({ open, currencySymbol, appSettings, onClose, onGenerated }) {
  const today = new Date();
  const [year, setYear]         = useState(today.getFullYear());
  const [format, setFormat]     = useState(appSettings?.receiptSettings?.receiptFormat || "itemized");
  const [eligible, setEligible] = useState(null);
  const [busy, setBusy]         = useState(false);
  const [progress, setProgress] = useState(null);   // { done, total, member, running }
  const [summary, setSummary]   = useState(null);
  const [error, setError]       = useState("");

  // Load eligible count when year changes.
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    setEligible(null);
    apiReceiptEligibleMembers(year)
      .then(r => { if (!cancel) setEligible(r); })
      .catch(() => { if (!cancel) setEligible({ eligibleMemberCount: 0, memberIds: [] }); });
    return () => { cancel = true; };
  }, [open, year]);

  if (!open) return null;

  const closeAndReset = () => {
    setYear(today.getFullYear()); setFormat(appSettings?.receiptSettings?.receiptFormat || "itemized");
    setEligible(null); setSummary(null); setError(""); setProgress(null);
    onClose && onClose();
  };

  const runBatch = async () => {
    if (!eligible?.eligibleMemberCount) return;
    setError(""); setBusy(true);
    setProgress({ done: 0, total: eligible.eligibleMemberCount, running: true });
    try {
      const res = await apiGenerateReceiptBatch({ year, format });
      // The backend returns final counts; simulate final progress step then show summary.
      setProgress({ done: res.totalMembers, total: res.totalMembers, running: false });
      setSummary(res);
      onGenerated && onGenerated();
    } catch (e) {
      setError(e?.message || "Batch failed");
    } finally { setBusy(false); }
  };

  const money = (c) => `${currencySymbol}${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && closeAndReset()}
      data-testid="finance-receipt-batch-modal"
      style={{ alignItems: "flex-start", paddingTop: 12, paddingBottom: 84 }}
    >
      <div className="modal" style={{ width: "min(96vw, 560px)", maxHeight: "calc(100vh - 96px)", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ position: "sticky", top: 0, background: "inherit", zIndex: 2 }}>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <CustomIcon name="document" size={18} /> Year-End Receipt Batch
          </h3>
          <button className="modal-close" onClick={closeAndReset} data-testid="finance-receipt-batch-close">×</button>
        </div>

        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          {!summary ? (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.75 }}>Year</label>
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    step="1"
                    className="form-input"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value) || today.getFullYear())}
                    data-testid="finance-receipt-batch-year"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, opacity: 0.75 }}>Format</label>
                  <select
                    className="form-input"
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                    data-testid="finance-receipt-batch-format"
                  >
                    <option value="itemized">Itemized</option>
                    <option value="total_only">Total Only</option>
                  </select>
                </div>
              </div>

              <div data-testid="finance-receipt-batch-preview" style={{
                padding: 14, borderRadius: 10, background: "color-mix(in srgb, var(--kc-accent) 12%, transparent)",
                border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", marginBottom: 14, textAlign: "center",
              }}>
                {eligible === null ? (
                  <div style={{ opacity: 0.7, fontSize: 13 }}>Calculating eligible members…</div>
                ) : (
                  <>
                    <div style={{ fontFamily: "'Cinzel',serif", fontSize: 32, fontWeight: 700, color: "var(--kc-accent)" }} data-testid="finance-receipt-batch-count">
                      {eligible.eligibleMemberCount}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                      member{eligible.eligibleMemberCount === 1 ? "" : "s"} have giving in <b>{year}</b>
                    </div>
                  </>
                )}
              </div>

              {progress && (
                <div style={{ marginBottom: 14 }} data-testid="finance-receipt-batch-progress">
                  <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4, textAlign: "center" }}>
                    {progress.done} of {progress.total} complete
                  </div>
                  <div style={{ height: 8, background: "rgba(255,255,255,0.08)", borderRadius: 999, overflow: "hidden" }}>
                    <div style={{
                      height: "100%",
                      width: progress.total ? `${Math.round((progress.done / progress.total) * 100)}%` : "0%",
                      background: "var(--kc-accent)",
                      transition: "width 0.25s ease",
                    }} />
                  </div>
                </div>
              )}

              {error && (
                <div data-testid="finance-receipt-batch-error" style={{ background: "rgba(248,113,113,0.15)", color: "#f87171", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
                  {error}
                </div>
              )}
            </>
          ) : (
            <div data-testid="finance-receipt-batch-summary">
              <div style={{ textAlign: "center", padding: "12px 8px" }}>
                <div style={{ fontSize: 46, color: "#4ade80" }}>✓</div>
                <div style={{ fontFamily: "'Cinzel',serif", fontSize: 20, fontWeight: 700, marginTop: 6 }}>Batch Complete</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8, marginTop: 10 }}>
                <StatBox label="Members" value={summary.totalMembers} testid="finance-receipt-batch-stat-total" />
                <StatBox label="Generated" value={summary.generated} color="#4ade80" testid="finance-receipt-batch-stat-generated" />
                <StatBox label="Failed" value={summary.failed} color="#f87171" testid="finance-receipt-batch-stat-failed" />
              </div>
              {summary.failed > 0 && (
                <div style={{ marginTop: 14, padding: 12, background: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 8 }} data-testid="finance-receipt-batch-failed-list">
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#f87171", marginBottom: 6 }}>Failed members:</div>
                  <ul style={{ margin: "4px 0 0 18px", fontSize: 12, color: "#f2a2a2" }}>
                    {summary.failures.slice(0, 10).map((f, i) => (
                      <li key={i}>{f.memberId} — {f.reason}</li>
                    ))}
                    {summary.failures.length > 10 && <li>… and {summary.failures.length - 10} more</li>}
                  </ul>
                </div>
              )}
              {summary.generated > 0 && (
                <div style={{ marginTop: 14, fontSize: 12, opacity: 0.85, background: "rgba(74,222,128,0.10)", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 8, padding: 10 }}>
                  Every generated member has received an in-app notification. Members can download their receipts from their My Giving page.
                </div>
              )}
              {summary.generated > 0 && (
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  Total dollar amount: <b style={{ color: "var(--kc-accent)" }}>
                    {money(summary.successes.reduce((s, r) => s + (r.totalCents || 0), 0))}
                  </b>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{
          position: "sticky", bottom: 0, background: "var(--kc-surface)",
          borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 22%, transparent)", padding: "12px 16px",
          display: "flex", gap: 10, alignItems: "center",
        }}>
          {!summary ? (
            <>
              <button className="btn btn-outline btn-sm" onClick={closeAndReset} data-testid="finance-receipt-batch-cancel">Cancel</button>
              <div style={{ flex: 1 }} />
              <button
                className="btn"
                data-testid="finance-receipt-batch-run"
                onClick={runBatch}
                disabled={busy || !eligible?.eligibleMemberCount}
                style={{
                  background: (!busy && eligible?.eligibleMemberCount) ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 35%, transparent)",
                  color: "#111", fontWeight: 800, minHeight: 44, padding: "0 22px", borderRadius: 10,
                  cursor: (busy || !eligible?.eligibleMemberCount) ? "not-allowed" : "pointer",
                }}
              >
                {busy ? "Generating…" : `Generate All Receipts${eligible?.eligibleMemberCount ? ` (${eligible.eligibleMemberCount})` : ""}`}
              </button>
            </>
          ) : (
            <button className="btn btn-primary" style={{ marginLeft: "auto" }} onClick={closeAndReset} data-testid="finance-receipt-batch-done">Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, color = "var(--kc-accent)", testid }) {
  return (
    <div
      data-testid={testid}
      style={{ padding: 10, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", textAlign: "center" }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "'Cinzel',serif" }}>{value}</div>
      <div style={{ fontSize: 10, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginTop: 2 }}>{label}</div>
    </div>
  );
}
